import React, { useMemo, useState } from 'react';

import classNames from 'classnames';

import ExposureTable from './ExposureTable';
import GanttView from './GanttView';
import { useProjectPlanReportContext } from './ProjectPlanReportContext';
import ProjectPlanReportTableRow from './ProjectPlanReportTableRow';
import TimelineView from './TimelineView';
import { buildAxis, computeDateBounds } from './viewModeUtils';

import Button from 'CommonComponents/Button';
import TableRowSkeleton from 'CommonComponents/TableRowSkeleton';

import ChevronDownIcon from 'Assets/icons/chevron-down.svg';
import ChevronRightIcon from 'Assets/icons/chevron-right.svg';
import FileExportIcon from 'Assets/icons/file-export.svg';

// Detailed value columns in render order, with their header labels. Selecting
// nothing in the criteria shows them all; otherwise only the chosen ones.
const DETAIL_COLUMNS = [
    { key: 'status', label: () => $t('Status') },
    { key: 'rag_status', label: () => $t('RAG') },
    { key: 'owner', label: () => $t('Owner') },
    { key: 'start_date', label: () => $t('Start date') },
    { key: 'target_completion_date', label: () => $t('Target date') },
    { key: 'estimated_completion_date', label: () => $t('Estimated date') },
    { key: 'variance', label: () => $t('Variance') },
];

/**
 * Resolve which detailed value columns are visible. When the criteria carry an
 * explicit visible_columns set, honour it; otherwise show every column.
 */
const resolveColumns = (visibleColumns) => {
    const all = !Array.isArray(visibleColumns) || visibleColumns.length === 0;
    return DETAIL_COLUMNS.reduce((acc, col) => {
        acc[col.key] = all || visibleColumns.includes(col.key);
        return acc;
    }, {});
};

const EmptyRow = ({ colSpan }) => (
    <tr className="tw-border-b-2 last:tw-border-0 tw-border-grey tw-bg-grey/50">
        <td colSpan={colSpan} className="tw-p-4">
            <span>{$t('No data available')}</span>
        </td>
    </tr>
);

/**
 * Detailed table: group header rows ({ group, _is_group }) interleaved with
 * activity rows. The styles.groups index map (1-based) classifies which rows
 * are section headers; clicking one collapses the activities beneath it.
 */
const DetailedTable = ({ rows, styles, loading, visibleColumns }) => {
    const [collapsed, setCollapsed] = useState({});

    const columns = useMemo(() => resolveColumns(visibleColumns), [visibleColumns]);
    const visibleCount = DETAIL_COLUMNS.filter((col) => columns[col.key]).length;
    const columnCount = 1 + visibleCount;

    const groupIndices = useMemo(() => new Set(styles?.groups ?? []), [styles]);

    const toggleGroup = (groupLabel) => setCollapsed((prev) => ({ ...prev, [groupLabel]: !prev[groupLabel] }));

    // Walk the flat rows, tracking the current section so activity rows can be
    // hidden when their group header is collapsed.
    let currentGroup = null;

    return (
        <table className="inbud-table inbud-table_first-sticky">
            <thead className="tw-sticky tw-top-0 tw-z-10">
                <tr className="*:!tw-bg-white *:!tw-align-middle *:tw-border-b-2 *:tw-border-r-2">
                    <th className="tw-z-10 tw-w-1/3 tw-min-w-72">{$t('Activity')}</th>
                    {DETAIL_COLUMNS.map(
                        (col) =>
                            columns[col.key] && (
                                <th key={col.key} className="tw-min-w-32">
                                    {col.label()}
                                </th>
                            ),
                    )}
                </tr>
            </thead>
            <tbody>
                {loading && <TableRowSkeleton rows={10} columns={columnCount} />}
                {!loading && rows.length === 0 && <EmptyRow colSpan={columnCount} />}
                {!loading &&
                    rows.length > 0 &&
                    rows.map((row, idx) => {
                        // pushEmptyRow-style indexed arrays are never produced here, but guard anyway.
                        if (Array.isArray(row)) return null;

                        const rowNumber = idx + 1;
                        const isGroup = row._is_group === true || groupIndices.has(rowNumber);

                        if (isGroup) {
                            currentGroup = row.group;
                            const isCollapsed = !!collapsed[currentGroup];

                            return (
                                <tr key={`group-${idx}`} className="tw-font-medium !tw-bg-charcoal-75 tw-cursor-pointer" onClick={() => toggleGroup(row.group)}>
                                    <td className="tw-text-white" colSpan={columnCount}>
                                        <div className="tw-flex tw-items-center tw-gap-x-1.5">
                                            {isCollapsed ? (
                                                <ChevronRightIcon className="tw-h-1.5 tw-w-1.5 tw-text-white" />
                                            ) : (
                                                <ChevronDownIcon className="tw-h-1.5 tw-w-1.5 tw-text-white" />
                                            )}
                                            <span>{row.group}</span>
                                        </div>
                                    </td>
                                </tr>
                            );
                        }

                        if (currentGroup !== null && collapsed[currentGroup]) {
                            return null;
                        }

                        return <ProjectPlanReportTableRow key={row.id ?? idx} row={row} columns={columns} />;
                    })}
            </tbody>
        </table>
    );
};

const SUMMARY_COLUMNS = [
    { key: 'total', label: () => $t('Total') },
    { key: 'not_started', label: () => $t('Not Started') },
    { key: 'in_progress', label: () => $t('In progress') },
    { key: 'complete', label: () => $t('Complete') },
    { key: 'overdue', label: () => $t('Overdue') },
    { key: 'exceptions', label: () => $t('Exceptions') },
    { key: 'milestones', label: () => $t('Milestones') },
    { key: 'key_milestones', label: () => $t('Key milestones') },
];

/**
 * Summary table: one aggregated row per group with status counts, overdue /
 * exception / milestone counts and a percent-complete bar.
 */
const SummaryTable = ({ rows, loading }) => {
    const columnCount = 1 + SUMMARY_COLUMNS.length + 1;

    return (
        <table className="inbud-table inbud-table_first-sticky">
            <thead className="tw-sticky tw-top-0 tw-z-10">
                <tr className="*:!tw-bg-white *:!tw-align-middle *:tw-border-b-2 *:tw-border-r-2">
                    <th className="tw-z-10 tw-w-1/4 tw-min-w-56">{$t('Group')}</th>
                    {SUMMARY_COLUMNS.map((col) => (
                        <th key={col.key} className="tw-min-w-24">
                            {col.label()}
                        </th>
                    ))}
                    <th className="tw-min-w-36 !tw-border-r-0">{$t('% Complete')}</th>
                </tr>
            </thead>
            <tbody>
                {loading && <TableRowSkeleton rows={10} columns={columnCount} />}
                {!loading && rows.length === 0 && <EmptyRow colSpan={columnCount} />}
                {!loading &&
                    rows.length > 0 &&
                    rows.map((row, idx) => (
                        <tr key={idx} className="tw-text-xs !tw-bg-white">
                            <td className="!tw-bg-charcoal-15 tw-text-charcoal">{row.group}</td>
                            {SUMMARY_COLUMNS.map((col) => (
                                <td key={col.key} className="inbud-numeric-cell">
                                    {row[col.key] ?? 0}
                                </td>
                            ))}
                            <td className="!tw-border-r-0">
                                <div className="tw-flex tw-items-center tw-gap-x-2">
                                    <div className="tw-flex-auto tw-h-1.5 tw-rounded-full tw-bg-charcoal-15 tw-overflow-hidden">
                                        <div className="tw-h-full tw-bg-green" style={{ width: `${row.percent_complete ?? 0}%` }} />
                                    </div>
                                    <span className="tw-text-xs tw-font-medium tw-text-charcoal">{row.percent_complete ?? 0}%</span>
                                </div>
                            </td>
                        </tr>
                    ))}
            </tbody>
        </table>
    );
};

/**
 * The Project Plan report table — summary or detailed, with a toolbar that
 * triggers the async Excel export.
 */
const ProjectPlanReportTable = () => {
    const { report, loading, criteria, exportReport, exporting } = useProjectPlanReportContext();

    const viewMode = criteria?.view_mode ?? 'table';
    const isSummary = (criteria?.detail_level ?? 'detailed') === 'summary';
    const rows = report?.rows ?? [];
    const styles = report?.styles ?? {};
    const timeView = criteria?.time_view ?? 'months';

    // Whether the visual axis was coarsened (Days → Months). Computed here so the
    // notice renders OUTSIDE the table frame — above the table wrapper — rather
    // than inside the chart's own bordered container.
    const axisCoarsened = useMemo(
        () => (viewMode === 'timeline' || viewMode === 'gantt' ? buildAxis(computeDateBounds(rows), timeView).coarsened : false),
        [viewMode, rows, timeView],
    );

    const hasData = rows.length > 0;

    // Excel export currently covers the tabular view only; the visual view-modes
    // are on-screen (PDF/Excel of them is out of scope for IB-674).
    const canExport = viewMode === 'table';

    const renderView = () => {
        switch (viewMode) {
            case 'timeline':
                return <TimelineView rows={rows} timeView={timeView} />;
            case 'gantt':
                return <GanttView rows={rows} timeView={timeView} />;
            case 'exposure':
                return <ExposureTable rows={rows} loading={loading} />;
            default:
                return isSummary ? (
                    <SummaryTable rows={rows} loading={loading} />
                ) : (
                    <DetailedTable rows={rows} styles={styles} loading={loading} visibleColumns={criteria?.visible_columns} />
                );
        }
    };

    return (
        <div className="tw-flex-auto tw-flex tw-flex-col tw-gap-y-4">
            {canExport && (
                <div className="tw-flex tw-justify-end">
                    <Button
                        variant="primary"
                        onClick={() => exportReport().catch(() => {})}
                        disabled={!hasData || loading || exporting}
                        loading={exporting}
                        className={classNames({ 'disabled:tw-bg-blue-25': true })}
                    >
                        <FileExportIcon className="tw-h-4 tw-w-4 tw-me-1.5" />
                        {$t('Download to Excel')}
                    </Button>
                </div>
            )}

            <div className="tw-flex-auto tw-flex tw-flex-col tw-gap-y-4 tw-min-h-0">
                {axisCoarsened && (
                    <div className="tw-text-2xs tw-text-charcoal-75 tw-bg-blue/5 tw-border tw-border-blue/25 tw-rounded-lg tw-px-3 tw-py-2">
                        {$t('The day-level axis was grouped into months because the selected range spans too many days to display.')}
                    </div>
                )}

                <div className="inbud-table_wrapper tw-flex-auto tw-min-h-0 tw-overflow-auto">{renderView()}</div>
            </div>
        </div>
    );
};

export default ProjectPlanReportTable;
